import { Button, Card, message, Modal, Popconfirm, Select, Space, Table, Typography } from "antd";
import { useEffect, useState } from "react";
import { serverFetch } from "../../../server";
import { selectFilterOption } from "../../../extensions/helper";
import { isMobile } from "@/extensions/design";
import "./user.css";

const { Text } = Typography;

const UserProducts = ({ id, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedProductVid, setSelectedProductVid] = useState(null);
  const [productCntList, setProductCntList] = useState([]);
  const [productCntLoading, setProductCntLoading] = useState(false);
  const [missingServiceNames, setMissingServiceNames] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const getData = () => {
    if (!id) return;
    setLoading(true);
    serverFetch(`User/ProductList/${id}`)
      .then(data => {
        setItems(data);
        setLoading(false);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  useEffect(getData, [id]);

  useEffect(() => {
    setProductLoading(true);
    serverFetch("product/ForSale?vid=1&active=true")
      .then(data => {
        setProducts(data);
        setProductLoading(false);
      })
      .catch(e => {
        message.error(e.userMessage);
        setProductLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!selectedProductId || (selectedProductVid !== 2 && selectedProductVid !== 3)) {
      setProductCntList([]);
      return;
    }
    setProductCntLoading(true);
    serverFetch(`product/CntList/${selectedProductId}`)
      .then(data => {
        setProductCntList(data || []);
        setProductCntLoading(false);
      })
      .catch(e => {
        message.error(e.userMessage);
        setProductCntList([]);
        setProductCntLoading(false);
      });
  }, [selectedProductId, selectedProductVid]);

  const onAdd = () => {
    if (!selectedProductId) {
      message.warning("Выберите услугу");
      return;
    }

    if (selectedProductVid === 2 || selectedProductVid === 3) {
      if (productCntLoading) {
        message.warning("Загрузка состава пакета...");
        return;
      }
    }

    setLoading(true);
    if (!productCntList.length) {
      serverFetch("User/ProductAdd", {
        method: "POST",
        bodyData: { userId: id, productId: selectedProductId }
      })
        .then(() => {
          const productName = products.find(p => p.id === selectedProductId)?.name || "";
          message.success(`Исполнитель добавлен в «${productName || selectedProductId}»`);
          setSelectedProductId(null);
          setSelectedProductVid(null);
          getData();
        })
        .catch(e => message.error(e.userMessage))
        .finally(() => setLoading(false));
      return;
    }

    serverFetch(`User/ProductList/${id}`)
      .then(userProducts => {
        const missingIds = productCntList
          .map(cnt => cnt.productId)
          .filter(pid => !userProducts.some(up => up.productId === pid));

        if (missingIds.length === 0) {
          return serverFetch("User/ProductAdd", {
            method: "POST",
            bodyData: { userId: id, productId: selectedProductId }
          }).then(() => {
            const productName = products.find(p => p.id === selectedProductId)?.name || "";
            message.success(`Исполнитель добавлен в «${productName || selectedProductId}»`);
            setSelectedProductId(null);
            setSelectedProductVid(null);
            getData();
          });
        }

        const namesMap = {};
        const firstPid = missingIds[0];
        const rest = missingIds.slice(1);
        return serverFetch(`product/${firstPid}`)
          .then(product => { namesMap[firstPid] = product.name || `#${firstPid}`; })
          .catch(() => { namesMap[firstPid] = `#${firstPid}`; })
          .then(() => {
            if (rest.length === 0) return;
            function fetchNext(index) {
              if (index >= rest.length) return;
              return serverFetch(`product/${rest[index]}`)
                .then(product => { namesMap[rest[index]] = product.name || `#${rest[index]}`; })
                .catch(() => { namesMap[rest[index]] = `#${rest[index]}`; })
                .then(() => fetchNext(index + 1));
            }
            return fetchNext(0);
          })
          .then(() => {
            setMissingServiceNames(namesMap);
            setModalVisible(true);
          });
      })
      .catch(e => message.error(e.userMessage))
      .finally(() => setLoading(false));
  };

  const handleModalOk = () => {
    setConfirmLoading(true);
    const missingIds = Object.keys(missingServiceNames).map(Number);
    function addNext(index) {
      if (index >= missingIds.length) {
        return serverFetch("User/ProductAdd", {
          method: "POST",
          bodyData: { userId: id, productId: selectedProductId }
        });
      }
      return serverFetch("User/ProductAdd", {
        method: "POST",
        bodyData: { userId: id, productId: missingIds[index] }
      }).then(() => addNext(index + 1));
    }
    addNext(0)
      .then(() => {
        const productName = products.find(p => p.id === selectedProductId)?.name || "";
        message.success(`Исполнитель добавлен в «${productName || selectedProductId}» и услуги`);
        setModalVisible(false);
        setMissingServiceNames({});
        setSelectedProductId(null);
        setSelectedProductVid(null);
        getData();
      })
      .catch(e => message.error(e.userMessage))
      .finally(() => setConfirmLoading(false));
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setMissingServiceNames({});
  };

  const onDelete = (userProductId) => {
    const record = items.find(i => i.id === userProductId);
    if (!record) return;

    const product = products.find(p => p.id === record.productId);
    const vid = product?.vid;

    if (vid === 2 || vid === 3) {
      serverFetch(`product/CntList/${record.productId}`)
        .then(cntList => {
          if (cntList.length) {
            const serviceIds = cntList.map(cnt => cnt.productId);
            const serviceUserProducts = items.filter(up => serviceIds.includes(up.productId));
            if (serviceUserProducts.length > 0) {
              const serviceNames = {};
              serviceUserProducts.forEach(up => {
                serviceNames[up.id] = up.productName || `#${up.productId}`;
              });
              setDeleteTarget({
                userProductId,
                userId: record.userId,
                serviceUserProductIds: serviceUserProducts.map(up => up.id),
                serviceNames,
              });
              setDeleteModalVisible(true);
            } else {
              performDelete(userProductId);
            }
          } else {
            performDelete(userProductId);
          }
        })
        .catch(e => message.error(e.userMessage));
      return;
    }

    performDelete(userProductId);
  };

  const performDelete = (userProductId) => {
    setLoading(true);
    serverFetch(`User/ProductDelete/${userProductId}`, { method: "DELETE" })
      .then(() => {
        getData();
        message.success("Услуга удалена");
      })
      .catch(e => message.error(e.userMessage))
      .finally(() => setLoading(false));
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setDeleteConfirmLoading(true);
    const serviceIds = deleteTarget.serviceUserProductIds;
    function deleteNext(index) {
      if (index >= serviceIds.length) {
        return serverFetch(`User/ProductDelete/${deleteTarget.userProductId}`, { method: "DELETE" });
      }
      return serverFetch(`User/ProductDelete/${serviceIds[index]}`, { method: "DELETE" }).then(() => deleteNext(index + 1));
    }
    deleteNext(0)
      .then(() => {
        setItems(prev => prev.filter(item => item.id !== deleteTarget.userProductId && !deleteTarget.serviceUserProductIds.includes(item.id)));
        setDeleteModalVisible(false);
        setDeleteTarget(null);
        getData();
        message.success("Услуга и связанные услуги пакета удалены");
      })
      .catch(e => message.error(e.userMessage))
      .finally(() => setDeleteConfirmLoading(false));
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeleteTarget(null);
  };

  const columns = [
    { title: "Наименование", dataIndex: "productName" },
    {
      title: "Действие",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title="Удалить услугу?"
          description="Это действие нельзя отменить."
          onConfirm={() => onDelete(record.id)}
          okText="Да"
          cancelText="Нет"
        >
          <Button size="small" danger>Удалить</Button>
        </Popconfirm>
      ),
    },
  ];

  return !isMobile ? (
    <div className="sport-section">
      <Space style={{ marginBottom: 16 }}>
        <Select
          style={{ width: 300 }}
          placeholder="Выберите услугу"
          options={products.map(p => ({ value: p.id, label: p.name }))}
          loading={productLoading}
          showSearch
          filterOption={selectFilterOption}
          value={selectedProductId}
          onChange={(value) => {
            setSelectedProductId(value);
            const p = products.find(pr => pr.id === value);
            setSelectedProductVid(p?.vid ?? null);
          }}
          allowClear
        />
        <Button onClick={onAdd} loading={loading} disabled={!selectedProductId || modalVisible || deleteModalVisible}>
          Добавить
        </Button>
      </Space>
      <Table
        columns={columns}
        size="small"
        dataSource={items}
        loading={loading}
        pagination={false}
        rowKey="id"
      />
      <Modal
        title="Подтверждение"
        open={modalVisible}
        onCancel={handleModalCancel}
        confirmLoading={confirmLoading}
        footer={[
          <Button key="cancel" onClick={handleModalCancel} disabled={confirmLoading}>
            Нет
          </Button>,
          <Button key="ok" type="primary" onClick={handleModalOk} loading={confirmLoading}>
            Да
          </Button>,
        ]}
      >
        <p>Пользователь не может оказывать услугу, указанную в пакете. Добавить его как исполнителя услуги?</p>
        {Object.keys(missingServiceNames).length > 0 && (
          <ul>
            {Object.entries(missingServiceNames).map(([pid, name]) => (
              <li key={pid}>{name}</li>
            ))}
          </ul>
        )}
      </Modal>
      <Modal
        title="Подтверждение"
        open={deleteModalVisible}
        onCancel={handleDeleteCancel}
        confirmLoading={deleteConfirmLoading}
        footer={[
          <Button key="cancel" onClick={handleDeleteCancel} disabled={deleteConfirmLoading}>
            Нет
          </Button>,
          <Button key="ok" type="primary" onClick={handleDeleteConfirm} loading={deleteConfirmLoading}>
            Да
          </Button>,
        ]}
      >
        <p>Пользователь является исполнителем следующих услуг из этого пакета. Удалить его из них тоже?</p>
        {deleteTarget && Object.keys(deleteTarget.serviceNames).length > 0 && (
          <ul>
            {Object.entries(deleteTarget.serviceNames).map(([id, name]) => (
              <li key={id}>{name}</li>
            ))}
          </ul>
        )}
      </Modal>
    </div>
  ) : (
    <div className="product-mobile-container">
      <div className="product-mobile-actions" style={{ display: "flex", gap: 8 }}>
        <Select
          style={{ flex: 1 }}
          placeholder="Выберите услугу"
          options={products.map(p => ({ value: p.id, label: p.name }))}
          loading={productLoading}
          showSearch
          filterOption={selectFilterOption}
          value={selectedProductId}
          onChange={(value) => {
            setSelectedProductId(value);
            const p = products.find(pr => pr.id === value);
            setSelectedProductVid(p?.vid ?? null);
          }}
          allowClear
        />
        <Button onClick={onAdd} loading={loading} disabled={!selectedProductId || modalVisible || deleteModalVisible}>
          Добавить
        </Button>
      </div>
      <div className="product-mobile-scroll">
        {items.map(item => (
          <Card key={item.id} className="product-packet-card-mobile" style={{ borderLeft: "4px solid #1677ff" }}>
            <div className="product-packet-card-row1-mobile">
              <Text strong>{item.productName}</Text>
              <Popconfirm
                title="Удалить услугу?"
                description="Это действие нельзя отменить."
                onConfirm={() => onDelete(item.id)}
                okText="Да"
                cancelText="Нет"
              >
                <Button size="small" danger>Удалить</Button>
              </Popconfirm>
            </div>
          </Card>
        ))}
        {items.length === 0 && !loading && <div className="product-packet-empty-mobile">Нет данных</div>}
      </div>
      <Modal
        title="Подтверждение"
        open={modalVisible}
        onCancel={handleModalCancel}
        confirmLoading={confirmLoading}
        footer={[
          <Button key="cancel" onClick={handleModalCancel} disabled={confirmLoading}>
            Нет
          </Button>,
          <Button key="ok" type="primary" onClick={handleModalOk} loading={confirmLoading}>
            Да
          </Button>,
        ]}
      >
        <p>Пользователь не может оказывать услугу, указанную в пакете. Добавить его как исполнителя услуги?</p>
        {Object.keys(missingServiceNames).length > 0 && (
          <ul>
            {Object.entries(missingServiceNames).map(([pid, name]) => (
              <li key={pid}>{name}</li>
            ))}
          </ul>
        )}
      </Modal>
      <Modal
        title="Подтверждение"
        open={deleteModalVisible}
        onCancel={handleDeleteCancel}
        confirmLoading={deleteConfirmLoading}
        footer={[
          <Button key="cancel" onClick={handleDeleteCancel} disabled={deleteConfirmLoading}>
            Нет
          </Button>,
          <Button key="ok" type="primary" onClick={handleDeleteConfirm} loading={deleteConfirmLoading}>
            Да
          </Button>,
        ]}
      >
        <p>Пользователь является исполнителем следующих услуг из этого пакета. Удалить его из них тоже?</p>
        {deleteTarget && Object.keys(deleteTarget.serviceNames).length > 0 && (
          <ul>
            {Object.entries(deleteTarget.serviceNames).map(([id, name]) => (
              <li key={id}>{name}</li>
            ))}
          </ul>
        )}
      </Modal>
    </div>
  );
};

export default UserProducts;
